/**
 * Server-state hooks for saved searches (C15 API) via TanStack Query.
 *
 * No hook owns a fetch effect: queries derive stable keys and mutations
 * invalidate the relevant keys. All requests go through {@link useApiClient}
 * so the Firebase ID token is injected uniformly.
 */
import {
  type CreateSavedSearchRequest,
  type ListSavedSearchesResponse,
  type RunSavedSearchResponse,
  type SavedSearch,
  type UpdateSavedSearchRequest,
} from "@records-monitor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/api/useApiClient";

export const savedSearchesKey = ["saved-searches"] as const;
export const savedSearchKey = (id: string): readonly string[] => ["saved-searches", id];

/** GET /saved-searches — the caller's saved searches. */
export function useSavedSearches() {
  const api = useApiClient();
  return useQuery({
    queryKey: savedSearchesKey,
    queryFn: () => api.get<ListSavedSearchesResponse>("/saved-searches"),
  });
}

/** GET /saved-searches/:id — a single saved search. */
export function useSavedSearch(id: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: savedSearchKey(id),
    queryFn: () => api.get<SavedSearch>(`/saved-searches/${id}`),
  });
}

/** POST /saved-searches — create a saved search from the current context. */
export function useCreateSavedSearch() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSavedSearchRequest) => api.post<SavedSearch>("/saved-searches", body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: savedSearchesKey });
    },
  });
}

/** PATCH /saved-searches/:id — edit schedule / query / prefs. */
export function useUpdateSavedSearch(id: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSavedSearchRequest) =>
      api.patch<SavedSearch>(`/saved-searches/${id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: savedSearchKey(id) });
      void queryClient.invalidateQueries({ queryKey: savedSearchesKey });
    },
  });
}

/** DELETE /saved-searches/:id. */
export function useDeleteSavedSearch() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<undefined>(`/saved-searches/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: savedSearchesKey });
    },
  });
}

/** POST /saved-searches/:id/run — run now; returns the delta summary. */
export function useRunSavedSearch(id: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<RunSavedSearchResponse>(`/saved-searches/${id}/run`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: savedSearchKey(id) });
    },
  });
}
